/**
 * Pure part of a group's day (see use-group-days.ts): the drops that go
 * today, in time order, with their label and rank; a drop fixed to a list
 * is counted apart; today's list names from the plan's group row.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanGroupRow } from '@trafficpulse/shared';

import type { DraftSlot } from '../../../../drop-generator.types';
import type { SlotTime } from '../../../../utils/slot-times';
import type { MatrixDrop } from '../list-choice/matrix-model';

export interface GroupDay {
  drops: MatrixDrop[];
  /** Today's lists, best first, when the plan knows them. */
  names: string[];
  /** Drops fixed to one list (they keep it whatever the choice). */
  fixed: number;
}

export interface TimedSlot {
  slot: DraftSlot;
  time: SlotTime;
}

/** A drop of today: timed, not cut by the end time, inside the window or lifted out of it. */
export const goesToday = (t: SlotTime | undefined): t is SlotTime => !!t && !t.clamped && (!t.outsideWindow || !!t.sentOutside);

export function groupDay(groupId: string, timed: TimedSlot[], label: (min: number) => string, row: DayPlanGroupRow | undefined): GroupDay {
  const mine = timed.filter((r) => r.slot.listGroupIds.includes(groupId));
  const free = mine.filter((r) => r.slot.listsByGroup?.[groupId] === undefined);
  const named = row?.listsToday ?? (row?.listOfDay ? [row.listOfDay] : []);
  return {
    drops: free.map((r) => ({ label: label(r.time.minute), rank: r.slot.listsRankByGroup?.[groupId] })),
    names: named.map((l) => l.name ?? `List #${l.wiserId}`),
    fixed: mine.length - free.length,
  };
}
